<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-vertical="end"
        >
            <DCol
                sm="2"
                class="pt-1"
            >
                <DInput
                    id="invoice-id"
                    v-model="pageFilters.invoiceId"
                    :label="__('tools.credit_card_payments.invoice_id')"
                    @keydown.enter.exact.prevent="changePage(transactions.current_page)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DInput
                    id="reference"
                    v-model="pageFilters.reference"
                    :label="__('tools.credit_card_payments.filters.reference')"
                    @keydown.enter.exact.prevent="changePage(transactions.current_page)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DInput
                    id="customer-number"
                    v-model="pageFilters.customerId"
                    :label="__('tools.credit_card_payments.filters.customer_number')"
                    @keydown.enter.exact.prevent="changePage(transactions.current_page)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="payment-status-dropdown"
                    v-model="pageFilters.transactionStatus"
                    :label="__('tools.credit_card_payments.filters.payment_status')"
                    :options="creditcardTransactionStatus"
                />
            </DCol>
            <DCol
                sm="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('tools.dunning.filters.filter')"
                    @click="changePage(transactions.current_page)"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DTable
            class="mt=3"
            :fields="getFields()"
            :items="transactions.data"
        >
            <template #invoice_id="{item}">
                <td v-if="userPermissions.includes('invoice.view')">
                    <Link :href="invoiceRoute(item)">
                        {{ item.invoice_id }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.invoice_id }}
                </td>
            </template>
            <template #customer_id="{item}">
                <td v-if="userPermissions.includes('customer.view')">
                    <Link :href="customerRoute(item.invoice.customer_id)">
                        {{ item.invoice.customer_id }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.invoice.customer_id }}
                </td>
            </template>
            <template #amount="{item}">
                <td
                    class="text-right"
                    :class="{'table-text-color-red': item.amount < 0}"
                >
                    {{ chargedAmount(item) }}
                </td>
            </template>
            <template #invoice_created_at="{item}">
                <td>{{ setDateFormat(item.invoice.created_at) }}</td>
            </template>
            <template #creditcard_number="{item}">
                <td>{{ item.credit_card?.number ?? '' }}</td>
            </template>
            <template #creditcard_owner="{item}">
                <td>{{ item.credit_card?.owner ?? '' }}</td>
            </template>
            <template #creditcard_expiry_date="{item}">
                <td v-if="item.credit_card">
                    {{ setDateFormat(item.credit_card?.expiry, 'YYYY-MM') }}
                </td>
                <td v-else />
            </template>
            <template #creditcard_status="{item}">
                <td v-if="item.credit_card">
                    {{ __(`tools.credit_card_payments.${creditCardStatus(item.credit_card)}`) }}
                </td>
                <td v-else />
            </template>
            <template #transaction_status="{item}">
                <td>
                    {{ __(`tools.credit_card_payments.status.${item.status}`) }}
                </td>
            </template>
            <template #message="{item}">
                <td>
                    <span
                        v-c-tooltip="tooltip(item)"
                        class="pointer-events"
                    >
                        {{ message(item) }}
                    </span>
                </td>
            </template>
            <template #created_at="{item}">
                <td>{{ setDateFormat(item.created_at) }}</td>
            </template>
        </DTable>
        <DPagination
            v-if="transactions.last_page > 1"
            :active-page="transactions.current_page"
            :pages="transactions.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>
</template>

<script>
import priceFormat from '~@/Mixins/priceFormat'
export default {
    name: 'CreditCardIndex',
    mixins: [
        priceFormat
    ],
    props: {
        transactions: {
            type: Object,
            default: () => {
                return {}
            }
        },
        filters: {
            type: Object,
            default: () => {
                return {
                    invoiceId: null,
                    reference: null,
                    customerId: null,
                    transactionStatus: null
                }
            }
        },
        creditcardTransactionStatus: {
            type: Array,
            default: () => {
                return []
            }
        }
    },
    data () {
        return {
            pageFilters: { ...this.filters }
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    methods: {
        creditCardStatus (creditCard) {
            return creditCard.is_active ? 'card_active' : 'card_inactive'
        },
        resetFilters () {
            const filters = this.pageFilters
            filters.invoiceId = null
            filters.reference = null
            filters.customerId = null
            filters.transactionStatus = null
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            queryParameters.page = 1
            this.$inertia.get(this.$route('tools.credit-card.transactions', {
                saveFilters: true,
                defaultFilters: true
            }), queryParameters)
        },
        getFields () {
            return [
                { key: 'invoice_id', label: this.__('tools.credit_card_payments.invoice_id') },
                { key: 'customer_id', label: this.__('tools.credit_card_payments.customer_id') },
                { key: 'amount', label: this.__('tools.credit_card_payments.amount') },
                { key: 'invoice_created_at', label: this.__('tools.credit_card_payments.invoice_created_at') },
                { key: 'creditcard_number', label: this.__('tools.credit_card_payments.creditcard_number') },
                { key: 'creditcard_owner', label: this.__('tools.credit_card_payments.creditcard_owner') },
                { key: 'creditcard_expiry_date', label: this.__('tools.credit_card_payments.creditcard_expiry_date') },
                { key: 'creditcard_status', label: this.__('tools.credit_card_payments.creditcard_status') },
                { key: 'transaction_status', label: this.__('tools.credit_card_payments.transaction_status') },
                { key: 'external_id', label: this.__('tools.credit_card_payments.external_id') },
                { key: 'number_of_tries', label: this.__('tools.credit_card_payments.number_of_tries') },
                { key: 'message', label: this.__('tools.credit_card_payments.message') },
                { key: 'created_at', label: this.__('tools.credit_card_payments.transaction_created_at') }
            ]
        },
        changePage (page) {
            const filters = this.pageFilters
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))

            if (page !== 1) {
                queryParameters.page = page
            }

            this.$inertia.get(this.$route('tools.credit-card.transactions', {
                saveFilters: true
            }), queryParameters)
        },
        customerRoute (value) {
            return this.$route('customers.customers.show', { customer: value })
        },
        invoiceRoute (value) {
            return this.$route('customers.customers.invoices.show', { customer: value.invoice.customer_id, invoice: value.invoice_id })
        },
        message (value) {
            return value.credit_card_refusal_reasons !== null ? `${value.credit_card_refusal_reasons.code}: ${value.credit_card_refusal_reasons.reason}` : ''
        },
        tooltip (value) {
            return value.credit_card_refusal_reasons !== null ? value.credit_card_refusal_reasons.description : ''
        },
        chargedAmount (item) {
            // undefined as well as null: a tenant whose migration has not run yet sends no amount key.
            if (item.amount === null || item.amount === undefined) {
                return '\u2014'
            }

            // The market region comes from the invoice, not the card: oneHome transactions have no
            // credit card, and optional chaining on it short-circuits the whole expression to
            // undefined, which getCurrencyCode() renders as 'N/A'. customers.market_region_id is
            // NOT NULL and invoice.customer is eager loaded, so it always resolves.
            return this.priceFormatForDisplay(item.amount, 2, item.invoice?.customer?.market_region_id)
        }

    }
}
</script>
